import React, { useState, useRef, useEffect } from 'react';
import ApiServices from '../services/ApiServices';
import { ChevronDown, Store } from 'lucide-react';

const StoreHeader = ({ activeTab, profileImage, onMenuToggle, showMenuButton }) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const [localStores, setLocalStores] = useState([]);
  const [localSelectedId, setLocalSelectedId] = useState(null);
  const [localCurrentName, setLocalCurrentName] = useState('Store Name');
  const dropdownRef = useRef(null);
  const role = localStorage.getItem('role');

  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      if (role === 'store_owner') {
        const stored = localStorage.getItem('stores');
        if (stored) {
          const parsed = JSON.parse(stored);
          setLocalStores(parsed);
          let selected = localStorage.getItem('selectedStoreId');
          if (!selected && parsed.length > 0) {
            selected = parsed[0].id.toString();
            localStorage.setItem('selectedStoreId', selected);
          }
          setLocalSelectedId(selected);
          const current = parsed.find((s) => s.id.toString() === selected);
          setLocalCurrentName(current ? current.name : 'Store Name');
        }
      } else if (role === 'store_manager' || role === 'superadmin') {
        let storeName = localStorage.getItem('storeName');
        if (storeName) {
          setLocalCurrentName(storeName);
        } else {
          const storedId = localStorage.getItem('storeId');
          if (storedId) {
            try {
              const response = await ApiServices.getData(`/stores/${storedId}`);
              storeName = response.data.name || 'Store Name';
              localStorage.setItem('storeName', storeName);
              setLocalCurrentName(storeName);
            } catch (error) {
              console.error('Error fetching store name:', error);
              setLocalCurrentName('Store Name');
            }
          } else {
            setLocalCurrentName('Store Name');
          }
        }
      }
    };
    fetchData();
  }, [role]);

  const isOwner = role === 'store_owner';
  const showDropdownFeature = isOwner && localStores.length > 0;

  return (
    <header className="fixed top-0 right-0 left-0 md:left-64 h-16 bg-white/80 backdrop-blur-md border-b border-gray-200 flex items-center justify-between px-4 md:px-8 z-30 select-none">
      <div className="flex items-center gap-3">
        {showMenuButton && (
          <button
            onClick={onMenuToggle}
            className="md:hidden p-1.5 rounded-lg hover:bg-gray-100 transition-colors"
            aria-label="Toggle menu"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="3" y1="6" x2="21" y2="6" />
              <line x1="3" y1="12" x2="21" y2="12" />
              <line x1="3" y1="18" x2="21" y2="18" />
            </svg>
          </button>
        )}
        <h2 className="text-lg md:text-xl font-semibold text-slate-800 leading-none tracking-tight">
          {activeTab}
        </h2>
      </div>

      <div className="relative" ref={dropdownRef}>
        <button
          type="button"
          className="flex items-center gap-2 text-gray-700 font-medium text-sm bg-transparent border-none cursor-pointer"
          onClick={showDropdownFeature ? () => setShowDropdown(!showDropdown) : undefined}
          aria-haspopup={showDropdownFeature ? "listbox" : undefined}
          aria-expanded={showDropdownFeature ? showDropdown : undefined}
          aria-label={`Current store: ${localCurrentName}`}
        >
          <Store className="w-4 h-4 text-gray-400" />
          <span className="truncate max-w-[120px] sm:max-w-[180px]">{localCurrentName}</span>
          {showDropdownFeature && (
            <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${showDropdown ? 'rotate-180' : ''}`} />
          )}
        </button>
        {showDropdown && (
          <ul role="listbox" aria-label="Select store" className="absolute top-full right-0 mt-2 bg-white border border-gray-200 rounded-xl shadow-lg z-20 min-w-[220px] max-h-[300px] overflow-y-auto list-none p-0 m-0">
            {localStores.map((store) => (
              <li
                key={store.id}
                role="option"
                aria-selected={store.id.toString() === localSelectedId}
                className={`px-4 py-3 cursor-pointer border-b border-gray-50 last:border-b-0 transition-colors text-sm
                  ${store.id.toString() === localSelectedId
                    ? 'bg-navy-50 text-navy-700 font-medium'
                    : 'hover:bg-gray-50 text-gray-700'
                  }`}
                onClick={() => {
                  const newId = store.id.toString();
                  localStorage.setItem('selectedStoreId', newId);
                  localStorage.setItem('lastActiveTab', activeTab);
                  setLocalSelectedId(newId);
                  setLocalCurrentName(store.name);
                  setShowDropdown(false);
                  window.location.href = `/store/${store.id}`;
                }}
              >
                {store.name}
              </li>
            ))}
          </ul>
        )}
      </div>
    </header>
  );
};

export default StoreHeader;
